import {
  Box,
  Divider,
  Text,
  VStack,
  HStack,
  Badge,
  Image,
  Link as ChakraLink,
  useColorModeValue,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { formatRelativeTime, RESEARCH_TYPES } from '../../utils/formatters';
import { Price } from '../common/Price';

const GRADIENT_BY_TYPE = {
  attribution: 'linear(to-br, purple.400, blue.500)',
  provenance: 'linear(to-br, blue.400, cyan.500)',
  identification: 'linear(to-br, teal.400, green.500)',
  technical: 'linear(to-br, cyan.400, blue.400)',
  heraldry: 'linear(to-br, orange.400, red.400)',
  archival: 'linear(to-br, yellow.400, orange.400)',
};

export const BountyCard = ({ bounty, to, actionButtons }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  const gradient = GRADIENT_BY_TYPE[bounty.researchType] || 'linear(to-br, gray.400, gray.600)';
  const typeInfo = RESEARCH_TYPES[bounty.researchType] || { label: 'Research', color: 'gray' };

  return (
    <Box
      bg={cardBg}
      rounded="xl"
      overflow="hidden"
      boxShadow="sm"
      borderWidth={1}
      borderColor={borderColor}
      _hover={{ boxShadow: 'lg', transform: actionButtons ? 'none' : 'translateY(-3px)' }}
      transition="all 0.2s"
      display="flex"
      flexDirection="column"
    >
      {/* Clickable area */}
      <Box
        as={Link}
        to={to || `/tasks/${bounty.id}`}
        display="block"
        _hover={{ textDecoration: 'none' }}
        flex="1"
      >
        {/* Image or gradient header */}
        <Box h="160px" position="relative" overflow="hidden">
          {bounty.thumbnailUrl ? (
            <Image
              src={bounty.thumbnailUrl}
              alt={bounty.title}
              w="full"
              h="full"
              objectFit="cover"
            />
          ) : (
            <Box w="full" h="full" bgGradient={gradient} />
          )}
          {/* Overlay badges */}
          <HStack position="absolute" top={3} left={3} right={3} justify="space-between" align="flex-start">
            <Badge
              colorScheme={typeInfo.color}
              bg={`${typeInfo.color}.100`}
              color={`${typeInfo.color}.800`}
              fontSize="xs"
              px={2}
              py={1}
              rounded="md"
            >
              {typeInfo.label}
            </Badge>
            <Box bg="white" rounded="lg" px={2} py={1} boxShadow="sm">
              <Text fontSize="sm" fontWeight="bold" color="green.600" lineHeight={1.3}>
                <Price cents={bounty.rewardAmount} size="sm" />
              </Text>
            </Box>
          </HStack>
        </Box>

        {/* Card body */}
        <Box px={4} py={3}>
          <HStack justify="space-between" align="center" spacing={2}>
            <VStack align="start" spacing={0} minW={0} flex="1">
              <Text fontSize="xs" color="gray.400">Posted by</Text>
              <ChakraLink
                as={Link}
                to={`/users/${bounty.creatorUid}`}
                state={{ from: 'bounties' }}
                fontSize="sm"
                fontWeight="medium"
                color="gray.600"
                _hover={{ color: 'orange.500', textDecoration: 'underline' }}
                onClick={(e) => e.stopPropagation()}
                noOfLines={1}
              >
                {bounty.creatorAnonymizedUsername}
              </ChakraLink>
            </VStack>
            <Text fontSize="xs" color="gray.400" flexShrink={0}>
              {formatRelativeTime(bounty.createdAt)}
            </Text>
          </HStack>
        </Box>
      </Box>

      {/* Action buttons footer */}
      {actionButtons && (
        <>
          <Divider />
          <Box px={4} py={2}>
            {actionButtons}
          </Box>
        </>
      )}
    </Box>
  );
};
